// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { ArrowLeft } from "lucide-react"
import { Link, Navigate, useParams } from "react-router-dom"
import { useForge } from "@/queries/queries.forges"
import { FullPageSpinnerWithText } from "@/components/ui/spinner"
import { CopyableId } from "@/views/forges/copyableId"
import { ForgeArena } from "@/views/forges/forgeArena"
import { ForgeSpec } from "@/views/forges/forgeSpec"
import { ForgeCycleHistory } from "@/views/evolutionCycles/forgeCycleHistory"

const ForgeDetailsView = () => {
  const { forgeId } = useParams<{ forgeId: string }>()

  if (!forgeId) {
    return <Navigate to="/forges" replace />
  }

  return <ForgeDetailsContent forgeId={forgeId} />
}

const ForgeDetailsContent = ({ forgeId }: { forgeId: string }) => {
  const { data, isLoading, isError } = useForge(forgeId)

  if (isLoading) {
    return <FullPageSpinnerWithText text="Loading forge" />
  }
  if (isError || !data) {
    return (
      <div
        role="alert"
        className="m-4 rounded-md border border-failed/40 bg-failed/10 p-4 text-failed-fg"
      >
        Could not load forge {forgeId}.
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[880px] flex-col gap-[74px] px-6 pb-16 pt-6">
      <header className="flex flex-col gap-[18px]">
        <div className="flex items-center justify-between gap-3">
          <Link
            to="/forges"
            className="inline-flex items-center gap-[7px] rounded-lg border border-border-strong bg-[rgba(7,30,35,0.4)] px-[11px] py-[5px] text-xs font-medium text-text-secondary transition-colors hover:border-border-active hover:text-foreground"
          >
            <ArrowLeft aria-hidden className="size-3.5" />
            My Forges
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-tertiary">
            Forge detail
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3.5">
          <h1 className="m-0 text-[30px] font-semibold leading-none tracking-[-0.015em] text-foreground">
            {data.name}
          </h1>
          <span className="inline-flex items-center gap-[7px] rounded-lg border border-border bg-surface px-[9px] py-1 font-mono text-[11px] text-text-secondary">
            <span className="select-text">{data.forge_id}</span>
            <CopyableId id={data.forge_id} label="forge id" />
          </span>
        </div>
      </header>

      <ForgeArena forgeId={data.forge_id} nCycles={data.n_cycles} />

      <ForgeCycleHistory forgeId={data.forge_id} />

      <ForgeSpec
        forgeId={data.forge_id}
        createdAt={data.created_at}
        specJson={data.spec_json}
      />
    </div>
  )
}

export default ForgeDetailsView
